import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, FileText, AlertTriangle, Download, Camera, ImagePlus } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { GreyBillForm } from '../components/forms';
import { useSettings } from '../contexts/SettingsContext';
import { exportBillToPDF } from '../lib/pdf-export';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillDetails = () => {
  const params = useParams();
  const billId = params['*'] || params.billId;
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();
  const { controls } = useSettings();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchBill();
  }, [billId]);

  const fetchBill = async () => {
    try {
      const response = await api.get(`/grey-bills/${billId}`);
      setBill(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load bill details');
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    fetchBill();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/grey-bills/${billId}`);
      toast.success('Bill deleted successfully!');
      navigate('/grey-bills');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bill');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="p-8 text-center">
        <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-600">Bill not found</p>
      </div>
    );
  }

  return (
    <div data-testid="bill-details-page" className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-mono">
              {bill.bill_number}
            </h1>
            <p className="text-slate-600 mt-1">Grey Bill Details</p>
          </div>
          <div className="flex gap-2">
            {hasModulePermission('grey_bills', 'export_pdf') && controls.enable_pdf_export && (
            <Button
              variant="outline"
              onClick={() => exportBillToPDF(bill, { order_number: bill.order_number })}
              title="Export as PDF"
              data-testid="bill-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            )}
            {hasModulePermission('grey_bills', 'edit') && (
            <Sheet open={isEditing} onOpenChange={setIsEditing}>
              <Button
                data-testid="edit-bill-btn"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Edit Grey Bill</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <GreyBillForm 
                    orderId={bill.order_id}
                    initialData={{
                      ...bill,
                      bill_id: billId,
                      order_rate: bill.order_rate,
                      order_unit: bill.order_unit,
                      order_number: bill.order_number
                    }}
                    mode="edit"
                    onSuccess={handleEditSuccess}
                    onCancel={() => setIsEditing(false)}
                  />
                </div>
              </SheetContent>
            </Sheet>
            )}
            {hasModulePermission('grey_bills', 'delete') && (
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button data-testid="delete-bill-btn" variant="destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Bill</DialogTitle>
                </DialogHeader>
                <p>Are you sure you want to delete this bill? This action cannot be undone.</p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Details Card */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle>Bill Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Order Number</p>
              {hasModulePermission('grey_orders', 'view') ? (
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-slate-100 font-mono"
                  onClick={() => navigate(`/grey-orders/${bill.order_id}`)}
                >
                  {bill.order_number}
                </Badge>
              ) : (
                <span className="font-mono font-medium text-slate-900">{bill.order_number}</span>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Purchase Bill Number</p>
              <p className="font-medium text-slate-900">{bill.purchase_bill_number}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Party Name</p>
              <p className="font-medium text-slate-900">{bill.party_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Bill Date</p>
              <p className="font-medium text-slate-900">{formatDate(bill.billed_date)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Billed Quantity</p>
              <p className="font-medium text-slate-900 text-lg">{formatNumber(bill.billed_quantity)} {bill.order_unit}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Order Rate</p>
              <p className="font-medium text-slate-900">₹{bill.order_rate || '-'} / meter</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Bill Rate</p>
              <div className="flex items-center gap-2">
                <p className={`font-medium ${bill.rate_mismatch ? 'text-amber-700' : 'text-slate-900'}`}>
                  {bill.bill_rate ? `₹${bill.bill_rate} / meter` : '-'}
                </p>
                {bill.rate_mismatch && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Mismatch
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Amount</p>
              <p className="font-medium text-slate-900">₹{formatNumber(bill.amount || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Freight Charges</p>
              <p className="font-medium text-slate-900">₹{formatNumber(bill.freight_charges || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Created By</p>
              <p className="font-medium text-slate-900">{bill.created_by_name}</p>
            </div>
            {bill.remarks && (
              <div className="md:col-span-2">
                <p className="text-sm text-slate-500 mb-1">Remarks</p>
                <p className="font-medium text-slate-900">{bill.remarks}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Attachments */}
      {(bill.transport_lr_photo || (bill.packing_slip_photos && bill.packing_slip_photos.length > 0)) && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle>Attachments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {bill.transport_lr_photo && (
              <div>
                <p className="text-sm text-slate-500 mb-2 flex items-center gap-1.5"><Camera className="h-4 w-4" />Transport LR Photo</p>
                <a href={`${API_URL}${bill.transport_lr_photo}`} target="_blank" rel="noopener noreferrer">
                  <img
                    src={`${API_URL}${bill.transport_lr_photo}`}
                    alt="Transport LR"
                    className="max-w-sm h-48 object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity cursor-pointer"
                    data-testid="transport-lr-photo"
                  />
                </a>
              </div>
            )}
            {bill.packing_slip_photos && bill.packing_slip_photos.length > 0 && (
              <div>
                <p className="text-sm text-slate-500 mb-2 flex items-center gap-1.5"><ImagePlus className="h-4 w-4" />Packing Slip Photos ({bill.packing_slip_photos.length})</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {bill.packing_slip_photos.map((photo, idx) => (
                    <a key={photo} href={`${API_URL}${photo}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`${API_URL}${photo}`}
                        alt={`Packing slip ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity cursor-pointer"
                        data-testid={`packing-slip-photo-${idx}`}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BillDetails;
